// React
import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Icon as IconMui } from "@material-ui/core";
import AvatarPlaceholder from "../AvatarPlaceholder";

/** Ignora "", "undefined", "null" vindos da API quando não há foto. */
function resolveImageSrc(src) {
  if (src == null || typeof src !== "string") return undefined;
  const trimmed = src.trim();
  if (!trimmed || trimmed === "undefined" || trimmed === "null") {
    return undefined;
  }
  return trimmed;
}

const Container = styled.div`
  min-width: ${({ size }) => size}px;
  max-width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: ${({ customColor, theme }) =>
    customColor || theme.color.primary.dark};
  border: 2px solid ${({ theme }) => theme.color.primary.main};
  overflow: hidden;
`;
const Icon = styled(IconMui)`
  color: white;
  && {
    font-size: ${({ size }) => size / 2}px;
  }
`;
const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${({ $contain }) => ($contain ? "contain" : "cover")};
  padding: ${({ $contain }) => ($contain ? "12%" : "0")};
  box-sizing: border-box;
  border-radius: 50%;
`;
const Name = styled(AvatarPlaceholder)`
  font-size: ${({ size }) => size / 2}px;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

function displayedImage(imageSrc, fallbackSrc) {
  const primary = resolveImageSrc(imageSrc);
  if (primary) return { imageSrc: primary, usingFallback: false };
  const fallback = resolveImageSrc(fallbackSrc);
  if (fallback) return { imageSrc: fallback, usingFallback: true };
  return { imageSrc: undefined, usingFallback: false };
}

class RoundedAvatar extends Component {
  constructor(props) {
    super(props);
    this.state = displayedImage(props.imageSrc, props.fallbackSrc);
  }

  componentDidUpdate(prevProps) {
    const { imageSrc, fallbackSrc } = this.props;
    if (
      prevProps.imageSrc === imageSrc &&
      prevProps.fallbackSrc === fallbackSrc
    ) {
      return;
    }
    if (prevProps.imageSrc !== imageSrc) {
      this.setState(displayedImage(imageSrc, fallbackSrc));
      return;
    }
    const { usingFallback, imageSrc: currentSrc } = this.state;
    const primary = resolveImageSrc(imageSrc);
    if (primary && !usingFallback) return;
    const nextFallback = resolveImageSrc(fallbackSrc);
    if (nextFallback !== currentSrc) {
      this.setState({
        imageSrc: nextFallback,
        usingFallback: Boolean(nextFallback)
      });
    }
  }

  handleErrorImg = () => {
    const { fallbackSrc } = this.props;
    const { usingFallback, imageSrc } = this.state;
    const fallback = resolveImageSrc(fallbackSrc);
    if (!usingFallback && fallback && fallback !== imageSrc) {
      this.setState({ imageSrc: fallback, usingFallback: true });
      return;
    }
    this.setState({ imageSrc: undefined, usingFallback: false });
  };

  render() {
    const { imageSrc, usingFallback } = this.state;
    const {
      size,
      imageAlt,
      fallbackAlt,
      icon,
      customColor,
      className,
      children
    } = this.props;

    let photoComponent = null;
    if (imageSrc) {
      photoComponent = (
        <Photo
          src={imageSrc}
          $contain={usingFallback}
          onError={this.handleErrorImg}
          alt={usingFallback && fallbackAlt ? fallbackAlt : imageAlt}
        />
      );
    } else if (children) {
      photoComponent = <Name size={size}>{children}</Name>;
    } else if (icon) {
      photoComponent = <Icon size={size}>{icon}</Icon>;
    }

    return (
      <Container size={size} className={className} customColor={customColor}>
        {photoComponent}
      </Container>
    );
  }
}

RoundedAvatar.defaultProps = {
  imageSrc: undefined,
  fallbackSrc: undefined,
  imageAlt: "Imagem de avatar arredondada",
  fallbackAlt: undefined,
  size: 44,
  customColor: "",
  icon: null,
  children: null
};

RoundedAvatar.propTypes = {
  imageSrc: PropTypes.string,
  fallbackSrc: PropTypes.string,
  imageAlt: PropTypes.string,
  fallbackAlt: PropTypes.string,
  icon: PropTypes.string,
  customColor: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  size: PropTypes.number, // Used in styles
  // eslint-disable-next-line react/require-default-props
  className: PropTypes.string, // Came from Styled Components
  children: PropTypes.string
};

export default RoundedAvatar;
